import React, { useState, useMemo } from "react";
import { Heading, Switch, Text } from "../../components";
import axios from "axios";
import { API_DOMAIN } from "config";
import CarCardTemplate from "components/CarCardTemplate";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { he } from "date-fns/locale";
import ContactDetails from "components/ContactDetails";
import OfferPaymentConfirmModal from "components/OfferPaymentConfirmModal";

registerLocale("he", he);

const MAX_PRICE_DIGITS = 7;
const AUCTION_DURATION_SECONDS = 5 * 60;

const toNumericPrice = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = typeof value === "number" ? value : Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(normalized) ? normalized : 0;
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const parseAuctionTime = (value) => {
  if (!value) return null;
  const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
  const time = new Date(normalized).getTime();
  return Number.isFinite(time) ? time : null;
};

const getAuctionEndValue = (state) =>
  state?.auction_active_until_iso ??
  state?.auction_end_time_iso ??
  state?.auction_active_until ??
  state?.auction_end_time ??
  state?.auction_end_at ??
  state?.end_time;

const getAuctionStartValue = (state) =>
  state?.auction_start_datetime_iso ??
  state?.auction_start_at_iso ??
  state?.auction_start_datetime ??
  state?.auction_start_at;

const isAuctionStatusEnded = (state) =>
  state?.has_ended === true || ["ended", "finalized"].includes(String(state?.status ?? state?.auction_status ?? "").toLowerCase());

const isAuctionStatusActive = (state) =>
  state?.has_started === true &&
  !isAuctionStatusEnded(state) &&
  ["active", "live"].includes(String(state?.status ?? state?.auction_status ?? "").toLowerCase());

const mergeAuctionTimerState = (previousState, nextState) => {
  if (!previousState || !nextState) return nextState;

  const previousEndMs = parseAuctionTime(getAuctionEndValue(previousState));
  const nextEndMs = parseAuctionTime(getAuctionEndValue(nextState));
  const nextEnded = nextState?.status === "ended" || nextState?.has_ended;

  if (!nextEnded && previousEndMs && nextEndMs && previousEndMs > nextEndMs) {
    const serverNow = nextState.server_now_iso || nextState.server_now || nextState.server_time || previousState.server_now_iso || previousState.server_now || previousState.server_time;
    const serverNowMs = parseAuctionTime(serverNow);
    return {
      ...nextState,
      auction_active_until: previousState.auction_active_until,
      auction_end_time: previousState.auction_end_time,
      auction_end_at: previousState.auction_end_at,
      auction_active_until_iso: previousState.auction_active_until_iso,
      auction_end_time_iso: previousState.auction_end_time_iso,
      server_now: serverNow,
      server_time: serverNow,
      server_now_iso: nextState.server_now_iso || previousState.server_now_iso,
      remaining_seconds: serverNowMs ? Math.max(0, Math.ceil((previousEndMs - serverNowMs) / 1000)) : previousState.remaining_seconds,
    };
  }

  return nextState;
};

const buildAcceptedAuctionState = (previousState, saved, offerPrice) => {
  const updatedEnd =
    saved?.auction_active_until ??
    saved?.auction_end_time ??
    saved?.auction_end_at ??
    previousState?.auction_active_until;

  return {
    ...(previousState || {}),
    ...(saved || {}),
    is_auction: true,
    status: "active",
    has_started: true,
    has_ended: false,
    can_bid: true,
    highest_offer: saved?.highest_offer ?? Number(offerPrice),
    highest_bid: saved?.highest_offer ?? saved?.highest_bid ?? Number(offerPrice),
    auction_active_until: updatedEnd,
    auction_end_time: saved?.auction_end_time ?? updatedEnd,
    auction_end_at: saved?.auction_end_at ?? updatedEnd,
    auction_active_until_iso: saved?.auction_active_until_iso ?? previousState?.auction_active_until_iso,
    auction_end_time_iso: saved?.auction_end_time_iso ?? previousState?.auction_end_time_iso,
    server_now: saved?.server_now ?? saved?.server_time ?? previousState?.server_now,
    server_time: saved?.server_time ?? saved?.server_now ?? previousState?.server_time,
    server_now_iso: saved?.server_now_iso ?? previousState?.server_now_iso,
    remaining_seconds: toFiniteNumber(saved?.remaining_seconds) ?? 300,
  };
};

export default function SendNewOfferPopup({ isRejectedOrApproved, onClose, car, currentUser, offer, onUpdateOffer }) {
  const [updatedOffer, setUpdatedOffer] = useState(null);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentConfirmMode, setPaymentConfirmMode] = useState("regular");
  const [paymentConfirmSnapshot, setPaymentConfirmSnapshot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImmediatePurchaseTooltip, setShowImmediatePurchaseTooltip] = useState(false);
  const [canHoverDevice, setCanHoverDevice] = useState(false);
  const [step, setStep] = useState(1);
  const [localOffer, setLocalOffer] = useState(offer);

  React.useEffect(() => {
    setLocalOffer(offer);
  }, [offer]);

  const [offerForm, setOfferForm] = useState({
    price: offer?.offered_price ?? "",
    offerDate: offer?.creation_date ? new Date(offer.creation_date) : new Date(),
    pickupDate: offer?.collection_date ? new Date(offer.collection_date) : "",
    lastPaymentDate: offer?.last_payment_date ? new Date(offer.last_payment_date) : "",
    reserve: false,
  });

  React.useEffect(() => {
    if (localOffer) {
      setOfferForm({
        price: localOffer?.offered_price ?? "",
        offerDate: localOffer?.creation_date ? new Date(localOffer.creation_date) : new Date(),
        pickupDate: localOffer?.collection_date ? new Date(localOffer.collection_date) : "",
        lastPaymentDate: localOffer?.last_payment_date ? new Date(localOffer.last_payment_date) : "",
        reserve: false,
      });
    }
  }, [localOffer]);

  const [errors, setErrors] = useState({ price: "" });

  const recommendedPrice = toNumericPrice(
    car?.recommended_price ?? car?.recommendedPrice ?? car?.asking_price ?? 0
  );
  const isAuctionCar = Number(car?.is_auction ?? car?.isAuction ?? 0) === 1 || car?.is_auction === true;
  const [auctionStatus, setAuctionStatus] = useState(null);
  const [auctionStatusFetchedAt, setAuctionStatusFetchedAt] = useState(0);
  const [auctionTick, setAuctionTick] = useState(Date.now());
  const currentOfferStatusForControls = Number(updatedOffer?.offer_status ?? offer?.offer_status ?? 0);
  const isFinalStatusForControls = currentOfferStatusForControls === 2 || currentOfferStatusForControls === 3;
  const minAllowedOffer = Math.ceil(recommendedPrice * 0.85);
  const offerValidationMessage =
    "סכום ההצעה נמוך מדי. ניתן להגיש הצעה עד 15% מתחת למחיר המומלץ";
  const immediatePurchaseValidationMessage =
    "ברכישה מיידית סכום ההצעה חייב להיות לפחות המחיר המומלץ";
  const auctionValidationMessage =
    "סכום ההצעה חייב להיות לפחות מחיר הפתיחה של המכרז";
  const auctionHigherThanCurrentMessage =
    "סכום ההצעה חייב להיות גבוה מההצעה הנוכחית במכרז";

  const hasOfferAboveRecommendedPrice = useMemo(() => {
    const explicitFlag =
      car?.has_offer_above_recommended_price ?? car?.hasOfferAboveRecommendedPrice;

    if (explicitFlag !== undefined && explicitFlag !== null) {
      return Number(explicitFlag) === 1 || explicitFlag === true;
    }

    const currentRecommendedPrice = toNumericPrice(
      car?.recommended_price ?? car?.recommendedPrice ?? car?.asking_price
    );

    if (currentRecommendedPrice <= 0) return false;

    const carOffers = [
      ...(Array.isArray(car?.offers) ? car.offers : []),
      ...(Array.isArray(car?.all_offers) ? car.all_offers : []),
      ...(Array.isArray(car?.allOffers) ? car.allOffers : []),
    ];

    return carOffers.some((item) => {
      const offeredPrice = toNumericPrice(
        item?.offered_price ?? item?.offeredPrice ?? item?.price
      );
      return offeredPrice > currentRecommendedPrice;
    });
  }, [car]);

  const immediatePurchaseUnavailableTooltip = isAuctionCar && !isFinalStatusForControls
    ? "ברכב במכרז ניתן להגיש הצעת מכרז בלבד."
    : "לא ניתן לבצע רכישה מיידית, כיוון שקיימת הצעה גבוהה מהמחיר המומלץ.";
  const isImmediatePurchaseUnavailable = (isAuctionCar && !isFinalStatusForControls) || hasOfferAboveRecommendedPrice;

  const isEdit = !!offer?.id || offer?.offer_status === 1;
  const isEditable = !isFinalStatusForControls;

  React.useEffect(() => {
    if (isImmediatePurchaseUnavailable && paymentConfirmMode === "immediatePurchase") {
      setPaymentConfirmMode("regular");
    }
  }, [isImmediatePurchaseUnavailable, paymentConfirmMode]);

  React.useEffect(() => {
    if (!isImmediatePurchaseUnavailable) {
      setShowImmediatePurchaseTooltip(false);
    }
  }, [isImmediatePurchaseUnavailable]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHoverDevice(mq.matches);

    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  React.useEffect(() => {
    if (!showImmediatePurchaseTooltip) return undefined;

    const closeTooltip = () => setShowImmediatePurchaseTooltip(false);

    window.addEventListener("scroll", closeTooltip, true);
    window.addEventListener("touchmove", closeTooltip, { passive: true });

    return () => {
      window.removeEventListener("scroll", closeTooltip, true);
      window.removeEventListener("touchmove", closeTooltip);
    };
  }, [showImmediatePurchaseTooltip]);

  const fetchAuctionStatus = React.useCallback(async () => {
    if (!isAuctionCar) {
      setAuctionStatus(null);
      return null;
    }

    const carId = Number(car?.car_id ?? car?.id);
    const resolvedUserId = Number(currentUser?.id ?? offer?.user_id ?? car?.user_id ?? offer?.userId ?? car?.userId) || null;
    if (!carId) return null;

    try {
      const statusRes = await axios.get(`${API_DOMAIN}/car/getAuctionStatus/${carId}`, {
        params: resolvedUserId ? { user_id: resolvedUserId } : {},
      });
      const nextStatus = statusRes?.data || null;
      setAuctionStatus((prev) => mergeAuctionTimerState(prev, nextStatus));
      setAuctionStatusFetchedAt(Date.now());
      return nextStatus;
    } catch (err) {
      setAuctionStatus(null);
      return null;
    }
  }, [car?.car_id, car?.id, car?.user_id, car?.userId, currentUser?.id, isAuctionCar, offer?.user_id, offer?.userId]);

  React.useEffect(() => {
    if (!isAuctionCar) return undefined;

    fetchAuctionStatus();
    const id = window.setInterval(fetchAuctionStatus, 15000);
    return () => window.clearInterval(id);
  }, [fetchAuctionStatus, isAuctionCar]);

  React.useEffect(() => {
    if (!isAuctionCar) return undefined;

    const id = window.setInterval(() => setAuctionTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isAuctionCar]);

  const effectiveOfferStatus = Number(updatedOffer?.offer_status ?? offer?.offer_status ?? 0);
  const isApproved = effectiveOfferStatus === 2;

  const auctionState = auctionStatus || (isAuctionCar ? car : null);
  const auctionStartMs = parseAuctionTime(getAuctionStartValue(auctionState));
  const auctionServerNowMs = parseAuctionTime(auctionStatus?.server_now_iso ?? auctionStatus?.server_now ?? auctionStatus?.server_time);
  const auctionRemainingSeconds = toFiniteNumber(auctionStatus?.remaining_seconds ?? auctionStatus?.seconds_until_end);
  const auctionEndMs =
    (auctionServerNowMs && auctionRemainingSeconds !== null
      ? auctionServerNowMs + auctionRemainingSeconds * 1000
      : null) ||
    parseAuctionTime(getAuctionEndValue(auctionState)) ||
    (auctionStartMs ? auctionStartMs + AUCTION_DURATION_SECONDS * 1000 : null);
  const auctionNowMs = auctionServerNowMs || Date.now();
  const secondsSinceAuctionFetch = auctionStatusFetchedAt && auctionServerNowMs
    ? Math.floor((auctionTick - auctionStatusFetchedAt) / 1000)
    : 0;
  const auctionSecondsUntilStart = Math.max(
    0,
    Number(
      auctionStartMs && auctionNowMs
        ? Math.ceil((auctionStartMs - auctionNowMs) / 1000)
        : auctionStatus?.seconds_until_start ?? 0
    ) - secondsSinceAuctionFetch
  );
  const auctionSecondsUntilEnd = Math.max(
    0,
    Number(auctionEndMs && auctionNowMs ? Math.ceil((auctionEndMs - auctionNowMs) / 1000) : 0) -
      secondsSinceAuctionFetch
  );
  const serverAuctionStateKnown = !!auctionStatus?.is_auction;
  const auctionHasStarted = serverAuctionStateKnown
    ? auctionStatus.has_started === true || isAuctionStatusActive(auctionStatus)
    : !!auctionStartMs && auctionSecondsUntilStart <= 0;
  const auctionHasEnded = serverAuctionStateKnown
    ? isAuctionStatusEnded(auctionStatus)
    : !!auctionStartMs && auctionHasStarted && auctionSecondsUntilEnd <= 0;
  const auctionIsActive = isAuctionCar && (serverAuctionStateKnown
    ? isAuctionStatusActive(auctionStatus)
    : auctionHasStarted && !auctionHasEnded);
  const isFinalOfferStatus = effectiveOfferStatus === 2 || effectiveOfferStatus === 3;
  const showAuctionPresentation = isAuctionCar && !isFinalOfferStatus;
  const auctionHighestOffer = toNumericPrice(
    auctionState?.highest_offer ?? auctionState?.highest_bid ?? car?.highest_offer ?? car?.max_offer
  );
  const finalAuctionWinningPrice = [
    updatedOffer?.auction_winning_amount,
    offer?.auction_winning_amount,
    car?.auction_winning_amount,
    auctionState?.auction_winning_amount,
    updatedOffer?.winning_price,
    offer?.winning_price,
    car?.winning_price,
    auctionState?.winning_price,
    updatedOffer?.approved_other_price,
    offer?.approved_other_price,
    car?.approved_other_price,
    auctionState?.approved_other_price,
    auctionState?.highest_offer,
    auctionState?.highest_bid,
    car?.highest_offer,
    car?.max_offer,
  ].map(toNumericPrice).find((price) => price > 0) ?? 0;
  const isFinalAuctionOffer = isAuctionCar && isFinalOfferStatus;
  const finalAuctionDisplayPrice = finalAuctionWinningPrice || toNumericPrice(offerForm.price);
  const auctionStartingPrice = auctionHighestOffer > 0 ? auctionHighestOffer : recommendedPrice;
  const auctionSubmitBlocked = showAuctionPresentation && (!auctionIsActive || !isEditable);
  const auctionBlockMessage = showAuctionPresentation
    ? !auctionHasStarted
      ? "המכרז טרם התחיל - ניתן יהיה להגיש הצעות כשהמכרז יתחיל"
      : auctionHasEnded
      ? "המכרז הסתיים"
      : ""
    : "";

  const contactData = useMemo(() => {
    const base = { ...(car || {}), ...(offer || {}), ...(updatedOffer || {}) };
    return {
      ...base,
      supplier_name: updatedOffer?.supplier_name ?? offer?.supplier_name ?? car?.supplier_name,
      contact_name: updatedOffer?.contact_name ?? offer?.contact_name ?? car?.contact_name,
      contact_phone: updatedOffer?.contact_phone ?? offer?.contact_phone ?? car?.contact_phone,
      contact_location: updatedOffer?.contact_location ?? offer?.contact_location ?? car?.contact_location,
    };
  }, [car, offer, updatedOffer]);

  const CustomInput = React.forwardRef(({ value, onClick, label }, ref) => (
    <div className="">
      <input
        ref={ref}
        readOnly
        onClick={onClick}
        value={value ?? ""}
        placeholder="__/__/__"
        className="w-40 bg-transparent text-right cursor-pointer border-0 border-b-0 ring-0 focus:ring-0 focus:outline-none placeholder:text-gray-400"
      />
      <span className="text-[16px] text-black-900_03 lg:text-[13px]">{label}</span>
    </div>
  ));
  CustomInput.displayName = "CustomInput";

  const handlePriceChange = (e) => {
    let v = String(e.target.value ?? "").replace(/\D/g, "");
    if (v.length > MAX_PRICE_DIGITS) v = v.slice(0, MAX_PRICE_DIGITS);
    setOfferForm((prev) => ({ ...prev, price: v }));
    validatePrice(v, paymentConfirmMode);
  };

  const validatePrice = (price = offerForm.price, mode = paymentConfirmMode) => {
    const raw = String(price ?? "").trim();
    const val = Number(raw);

    if (!raw) {
      setErrors({ price: "יש להזין סכום הצעה" });
      return false;
    }
    if (!Number.isFinite(val) || val <= 0) {
      setErrors({ price: "יש להזין סכום תקין" });
      return false;
    }
    if (recommendedPrice > 0) {
      const minPrice = isAuctionCar || mode === "immediatePurchase" ? recommendedPrice : minAllowedOffer;
      const message =
        isAuctionCar
          ? auctionValidationMessage
          : mode === "immediatePurchase"
          ? immediatePurchaseValidationMessage
          : offerValidationMessage;

      if (val < minPrice) {
        setErrors({ price: message });
        return false;
      }
    }
    if (isAuctionCar && auctionHighestOffer > 0 && val <= auctionHighestOffer) {
      setErrors({ price: auctionHigherThanCurrentMessage });
      return false;
    }
    setErrors({ price: "" });
    return true;
  };

  const handleImmediatePurchaseChange = (checked) => {
    if (isImmediatePurchaseUnavailable) return;

    const mode = checked ? "immediatePurchase" : "regular";
    setPaymentConfirmMode(mode);

    if (checked || errors.price === immediatePurchaseValidationMessage) {
      validatePrice(offerForm.price, mode);
    }
  };

  const fmt = (d) => (d ? new Date(d).toISOString().slice(0, 19).replace("T", " ") : null);

  const openPaymentConfirm = async (mode = paymentConfirmMode) => {
    if (isSubmitting || paymentConfirmOpen) return;
    const nextMode = isAuctionCar ? "regular" : mode;
    if (!validatePrice(offerForm.price, nextMode)) return;

    if (isAuctionCar) {
      const latestStatus = await fetchAuctionStatus();
      const latestStartMs = parseAuctionTime(getAuctionStartValue(latestStatus));
      const latestNowMs = latestStatus?.server_now_iso || latestStatus?.server_now
        ? parseAuctionTime(latestStatus.server_now_iso ?? latestStatus.server_now)
        : Date.now();
      const latestRemainingSeconds = toFiniteNumber(latestStatus?.remaining_seconds ?? latestStatus?.seconds_until_end);
      const latestEndMs =
        (latestNowMs && latestRemainingSeconds !== null
          ? latestNowMs + latestRemainingSeconds * 1000
          : null) ||
        parseAuctionTime(getAuctionEndValue(latestStatus)) ||
        (latestStartMs ? latestStartMs + AUCTION_DURATION_SECONDS * 1000 : null);
      const latestHasStarted = latestStatus?.is_auction
        ? latestStatus.has_started === true || isAuctionStatusActive(latestStatus)
        : !!latestStartMs && latestNowMs >= latestStartMs;
      const latestHasEnded = latestStatus?.is_auction
        ? isAuctionStatusEnded(latestStatus)
        : !!latestEndMs && latestNowMs >= latestEndMs;

      if (!latestStatus?.is_auction || !latestHasStarted || latestHasEnded) {
        const message = !latestHasStarted
          ? "המכרז טרם התחיל - ניתן יהיה להגיש הצעות כשהמכרז יתחיל"
          : "המכרז הסתיים";
        setErrors({ price: message });
        return;
      }
      const latestHighest = toNumericPrice(latestStatus?.highest_offer ?? latestStatus?.highest_bid);
      if (latestHighest > 0 && Number(offerForm.price) <= latestHighest) {
        setErrors({ price: auctionHigherThanCurrentMessage });
        return;
      }
    }

    setPaymentConfirmSnapshot({
      offerAmount: offerForm.price,
      minimumOfferPrice: isAuctionCar ? auctionStartingPrice : recommendedPrice,
    });
    setPaymentConfirmMode(nextMode);
    setPaymentConfirmOpen(true);
  };

  const submitOffer = async (paymentContext) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const carId = Number(car?.car_id ?? car?.id);
    const currentOfferId = Number(offer?.offer_id ?? offer?.id);
    const resolvedUserId = Number(currentUser?.id) || null;

    const payload = {
      car_id: carId,
      user_id: resolvedUserId,
      offered_price: Number(offerForm.price || recommendedPrice || 0),
      collection_date: fmt(offerForm.pickupDate),
      last_payment_date: fmt(offerForm.lastPaymentDate),
      is_reserved: paymentContext?.immediate_purchase ? 0 : offerForm.reserve ? 1 : 0,
      creation_date: offerForm.offerDate.toISOString().slice(0, 19).replace("T", " "),
      ...(paymentContext || {}),
    };

    try {
      let saveResponse = null;
      if (currentOfferId) {
        saveResponse = await axios.post(`${API_DOMAIN}/offer/updateOffer/${currentOfferId}`, payload);
      } else {
        saveResponse = await axios.post(`${API_DOMAIN}/offer/addOffer`, payload);
      }
      const saved = saveResponse?.data || {};

      const resp = await axios.get(`${API_DOMAIN}/offer/getUserOffer`, {
        params: { car_id: carId, user_id: resolvedUserId },
      });
      const raw = resp?.data;
      const fresh = Array.isArray(raw) ? raw[0] : raw?.offer ? raw.offer : raw;

      const upd = {
        ...(fresh || {}),
        id: Number(fresh?.id ?? fresh?.offer_id ?? saved?.offer_id ?? currentOfferId) || undefined,
        offer_id: Number(fresh?.offer_id ?? fresh?.id ?? saved?.offer_id ?? currentOfferId) || undefined,
        car_id: carId,
        offered_price: Number(fresh?.offered_price ?? offerForm.price),
        collection_date: fresh?.collection_date ?? fmt(offerForm.pickupDate),
        last_payment_date: fresh?.last_payment_date ?? fmt(offerForm.lastPaymentDate),
        is_reserved: Number(fresh?.is_reserved ?? (paymentContext?.immediate_purchase ? 0 : offerForm.reserve ? 1 : 0)),
        offer_status: Number(
          saved?.offer_status ??
          fresh?.offer_status ??
          fresh?.status ??
          (paymentContext?.immediate_purchase ? 2 : offer?.offer_status ?? 1)
        ),
        immediate_purchase: Number(saved?.immediate_purchase ?? fresh?.immediate_purchase ?? paymentContext?.immediate_purchase ?? 0),
        immediate_purchase_transaction_id: fresh?.immediate_purchase_transaction_id ?? paymentContext?.immediate_purchase_transaction_id,
        offer_position: fresh?.offer_position ?? fresh?.offerPosition,
        reservation_hold_id: fresh?.reservation_hold_id ?? paymentContext?.reservation_hold_id ?? offer?.reservation_hold_id,
        payment_method_id: fresh?.payment_method_id ?? paymentContext?.payment_method_id ?? offer?.payment_method_id,
        highest_offer: saved?.highest_offer ?? fresh?.highest_offer,
      };
      if (isAuctionCar) {
        setAuctionStatus((prev) => buildAcceptedAuctionState(prev, saved, offerForm.price));
        setAuctionStatusFetchedAt(Date.now());
        fetchAuctionStatus();
      }

      setUpdatedOffer(upd);
      setLocalOffer(upd);
      onUpdateOffer?.(upd);
      setPaymentConfirmOpen(false);
      setPaymentConfirmSnapshot(null);
      if (paymentContext?.immediate_purchase) {
        onClose?.();
        return;
      }
      setStep(2);
    } catch (err) {
      const serverMessage = err?.response?.data?.error;
      if (serverMessage) {
        setErrors({ price: serverMessage });
        return;
      }
      alert("אירעה שגיאה בשמירת ההצעה");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="popup-wrapper snop-root">
      <div className="popup-offer-container snop-container">
        <button className="x-close" onClick={onClose}>✕</button>

        <OfferPaymentConfirmModal
          open={paymentConfirmOpen}
          userId={Number(currentUser?.id ?? offer?.user_id ?? car?.user_id ?? offer?.userId ?? car?.userId)}
          car={car}
          offerAmount={paymentConfirmSnapshot?.offerAmount ?? offerForm.price}
          recommendedPrice={recommendedPrice}
          minimumOfferPrice={paymentConfirmSnapshot?.minimumOfferPrice}
          wantsReservation={showAuctionPresentation || (paymentConfirmMode === "regular" && offerForm.reserve)}
          mode={paymentConfirmMode}
          onClose={() => {
            setPaymentConfirmOpen(false);
            setPaymentConfirmSnapshot(null);
          }}
          onSubmitConfirmed={submitOffer}
          offerId={localOffer?.offer_id ?? localOffer?.id ?? offer?.offer_id ?? offer?.id}
          existingReservationHoldId={localOffer?.reservation_hold_id}
          existingPaymentMethodId={localOffer?.payment_method_id}
        />

        {step === 1 && (
          <div>
            <h2 className="add-car-title snop-title" style={{ color: "black", marginBottom: "1.5rem" }}>
              {effectiveOfferStatus !== 2 && effectiveOfferStatus !== 3 && (
                <>{showAuctionPresentation ? "הגש הצעה במכרז" : isEdit ? "עדכן את ההצעה שלך" : "הגש הצעה חדשה"}</>
              )}
            </h2>

            <div className="snop-layout flex items-start self-stretch sm:flex-col">
              <div className="new-offer-form snop-form mt-3.5 flex flex-1 flex-col items-start sm:self-stretch">
                <div className="flex flex-col gap-3.5">
                  {showAuctionPresentation && (
                    <div className="w-full text-right font-heebo" dir="rtl">
                      <div className="text-[16px] font-semibold text-black">
                        הצעות החל מ־₪{Number(auctionStartingPrice || 0).toLocaleString("he-IL")}
                      </div>
                      {auctionBlockMessage && (
                        <div className="mt-1 text-[14px] font-semibold text-red-600">
                          {auctionBlockMessage}
                        </div>
                      )}
                    </div>
                  )}

                  {/* מחיר מומלץ */}
                  {!showAuctionPresentation && (
                  <div className="min-price-display flex justify-end w-full" data-testid="min-price-display">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-red-600 font-semibold text-[18px]">
                        ₪{Number(isFinalAuctionOffer ? finalAuctionDisplayPrice : recommendedPrice ?? 0).toLocaleString("he-IL")}
                      </span>
                      <span className="text-[16px] text-black-900_03 lg:text-[13px]">
                        {isFinalAuctionOffer ? "מחיר זכיה" : "מחיר מומלץ"}
                      </span>
                    </div>
                  </div>
                  )}

                  {/* מחיר */}
                  <>
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-end justify-end gap-[7px]">
                      <Heading as="h3" className="!font-heebo text-[18px] font-bold">₪</Heading>
                      <input
                        type="number"
                        name="price"
                        value={offerForm.price}
                        onChange={handlePriceChange}
                        inputMode="numeric"
                        className={`w-32 border-0 border-b ${errors.price ? "border-red-500" : "border-blue_gray-400"} bg-transparent text-right focus:border-black focus:outline-none`}
                        disabled={!isEditable}
                      />
                    </div>
                    {errors.price && (
                      <Text className="text-red-600 text-sm">{errors.price}</Text>
                    )}
                  </div>

                  {/* תאריכים */}
                  <div className="flex flex-col gap-3.5 md:flex-row items-end justify-end">
                    <DatePicker
                      style={{ zIndex: "9999" }}
                      popperClassName="my-datepicker-popper"
                      locale="he"
                      selected={offerForm.offerDate}
                      dateFormat="dd/MM/yyyy"
                      calendarStartDay={0}
                      disabled
                      customInput={<CustomInput label=":תאריך ההצעה" />}
                      popperContainer={({ children }) => <div style={{ zIndex: 99999 }}>{children}</div>}
                      popperPlacement="bottom-start"
                    />
                    <DatePicker
                      locale="he"
                      style={{ zIndex: "99" }}
                      withPortal
                      popperClassName="my-datepicker-popper"
                      selected={offerForm.pickupDate}
                      onChange={(d) => setOfferForm((p) => ({ ...p, pickupDate: d }))}
                      dateFormat="dd/MM/yyyy"
                      calendarStartDay={0}
                      customInput={<CustomInput label=":תאריך איסוף" />}
                      popperContainer={({ children }) => <div style={{ zIndex: 99999 }}>{children}</div>}
                      popperPlacement="bottom-start"
                      disabled={!isEditable}
                      minDate={new Date()}
                    />
                    <DatePicker
                      locale="he"
                      withPortal
                      selected={offerForm.lastPaymentDate}
                      popperClassName="my-datepicker-popper"
                      onChange={(d) => setOfferForm((p) => ({ ...p, lastPaymentDate: d }))}
                      dateFormat="dd/MM/yyyy"
                      calendarStartDay={0}
                      customInput={<CustomInput label=":תאריך סיום תשלום" />}
                      popperContainer={({ children }) => <div style={{ zIndex: 1000 }}>{children}</div>}
                      popperPlacement="bottom-start"
                      disabled={!isEditable}
                      minDate={new Date()}
                    />
                  </div>

                  {/* רכישה מידית */}
                  {!showAuctionPresentation && (
                  <div
                    className="relative"
                    data-testid="immediate-purchase-action"
                    dir="rtl"
                    onPointerEnter={(e) => {
                      if (e.pointerType === "mouse" && isImmediatePurchaseUnavailable) {
                        setShowImmediatePurchaseTooltip(true);
                      }
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType === "mouse") {
                        setShowImmediatePurchaseTooltip(false);
                      }
                    }}
                    style={{
                      zIndex: showImmediatePurchaseTooltip ? 100000 : "auto",
                    }}
                  >
                    {isImmediatePurchaseUnavailable && showImmediatePurchaseTooltip && (
                      <div
                        role="tooltip"
                        className={`rounded-[12px] border border-gray-200 bg-white p-3 text-right font-heebo text-[15px] font-medium leading-[1.5] text-black shadow-[0_14px_32px_rgba(0,0,0,0.16)] ${
                          canHoverDevice
                            ? "absolute bottom-[calc(100%+12px)] right-1/2 translate-x-1/2"
                            : "relative"
                        }`}
                        style={{
                          backgroundColor: "#fff",
                          maxWidth: "100%",
                          width: "min(250px, 100%)",
                          zIndex: 100001,
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FDECEC] text-[16px] font-bold leading-none text-[#CE0000]">
                            !
                          </span>
                          <span>{immediatePurchaseUnavailableTooltip}</span>
                        </div>
                        <span className="absolute -bottom-1 right-1/2 h-2 w-2 translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white" />
                      </div>
                    )}
                    <div
                      className="relative flex  justify-start gap-3"
                      style={{
                        cursor: isImmediatePurchaseUnavailable ? "not-allowed" : undefined,
                      }}
                    >
                      <span
                        className={`text-[16px] text-black-900_03 lg:text-[13px] ${
                          isImmediatePurchaseUnavailable ? "opacity-60" : ""
                        }`}
                      >
                        רכישה מידית
                      </span>
                      <Switch
                        checked={paymentConfirmMode === "immediatePurchase"}
                        onCheckedChange={handleImmediatePurchaseChange}
                        disabled={!isEditable || isSubmitting || paymentConfirmOpen || isImmediatePurchaseUnavailable}
                        className={`immediate-purchase-switch ${
                          isImmediatePurchaseUnavailable ? "opacity-60" : ""
                        }`}
                        size="sm"
                      />
                      {isImmediatePurchaseUnavailable && (
                        <button
                          type="button"
                          aria-label={immediatePurchaseUnavailableTooltip}
                          className="absolute inset-0 z-10 cursor-not-allowed bg-transparent"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowImmediatePurchaseTooltip((prev) => !prev);
                          }}
                        />
                      )}
                    </div>
                  </div>
                  )}

                  {isApproved && (
                    <div className="w-full flex justify-center relative mt-4">
                      <ContactDetails
                        data={contactData}
                        buttonClassName="contacts-button"
                        tooltipWrapperClassName="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[99999]"
                      />
                    </div>
                  )}

                  {effectiveOfferStatus !== 2 && effectiveOfferStatus !== 3 && (
                    <div style={{ display: "flex", justifyContent: "end", marginTop: "1rem" }}>
                      <button
                        className="z-10 min-w-[128px] gap-2 rounded-[16px] px-3 font-semibold text-white snop-submitBtn"
                        style={{ background: "black", color: "white", padding: "5px", width: "40%" }}
                        onClick={() => openPaymentConfirm()}
                        disabled={isSubmitting || paymentConfirmOpen || auctionSubmitBlocked}
                      >
                        {showAuctionPresentation
                          ? "הגש הצעה במכרז"
                          : paymentConfirmMode === "immediatePurchase"
                          ? "לרכישה"
                          : isEdit ? "עדכן הצעת מחיר" : "שלח הצעת מחיר"}
                      </button>
                    </div>
                  )}
                  </>
                </div>
              </div>

              <div
                className="snop-card mt-3 flex shrink-0 justify-center hide-mobile"
                style={{ width: "300px" }}
              >
                <div className="w-full">
                  <CarCardTemplate
                    key={car?.id}
                    offer_status={offer?.offer_status ?? 0}
                    isRejectedOrApproved={isRejectedOrApproved}
                    {...car}
                    {...(isAuctionCar && auctionState ? auctionState : {})}
                    is_auction={isAuctionCar ? 1 : 0}
                    highest_offer={isFinalAuctionOffer ? finalAuctionWinningPrice : auctionHighestOffer}
                    className="w-full max-w-[300px] self-center"
                    offered_price={isFinalAuctionOffer ? finalAuctionDisplayPrice : offerForm.price}
                    offer_price_label={isFinalAuctionOffer ? "מחיר זכיה" : undefined}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col items-center justify-center h-full">
            <h2 className="add-car-title">!!!מזל טוב</h2>
            <h2 style={{ color: "black", marginBottom: "1rem" }} className="add-car-title">
              {isEdit ? "ההצעה שלך עודכנה בהצלחה" : "ההצעה שלך נשלחה בהצלחה"}
            </h2>
            <div className="flex justify-center w-full">
              <CarCardTemplate
                key={car?.id}
                {...car}
                {...(isAuctionCar && auctionState ? auctionState : {})}
                is_auction={isAuctionCar ? 1 : 0}
                highest_offer={isFinalAuctionOffer ? finalAuctionWinningPrice : auctionHighestOffer}
                offer_status={updatedOffer?.offer_status ?? 1}
                offered_price={isFinalAuctionOffer ? finalAuctionDisplayPrice : offerForm.price}
                offer_price_label={isFinalAuctionOffer ? "מחיר זכיה" : undefined}
                className="w-full max-w-[300px] sm:w-[70%] self-center"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
